/**
 * src/shell/command_palette.js
 *
 * Cmd/Ctrl+K command palette: fuzzy-searchable nav items (label + route).
 * Modal opens on Cmd/Ctrl+K (or via openCommandPalette() from the top-bar
 * search box), navigates on Enter, closes on Esc/click-outside.
 * Keyboard-accessible (arrow keys to move, Enter to select).
 *
 * THEMED (DESIGN_SPEC §4.6): all colors resolve to CSS custom properties from
 * styles/tokens.css, so the palette inherits the active light/dark theme
 * instead of the old dark-only hardcoded hex literals.
 */

import { navigate } from '../lib/router.js';
import { t } from '../i18n/t.js';

let paletteAbort = null;
// Latest mount's open() — lets the shell's header search box open the palette
// without re-implementing the keydown path (openCommandPalette below).
let _openPalette = null;

/**
 * Simple fuzzy search: returns items where all search chars appear in order.
 * Case-insensitive; scores by position (earlier matches = higher score).
 */
function fuzzyScore(text, query) {
  if (!query) return 0;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let lastIndex = -1;
  let score = 0;
  for (const char of q) {
    const idx = lower.indexOf(char, lastIndex + 1);
    if (idx === -1) return -1;
    score -= idx - lastIndex; // Tighter sequences = higher score
    lastIndex = idx;
  }
  return score;
}

function createPaletteDOM() {
  // Runs at OPEN time (not mount time), so t() resolves against the active
  // language — the placeholder flips with the language toggle.
  const html = `
    <div id="command-palette-overlay" class="command-palette-overlay">
      <div class="command-palette-modal">
        <div class="command-palette-header">
          <input
            type="text"
            id="command-palette-input"
            class="command-palette-input"
            placeholder="${escapeAttrCp(t('palette.searchPlaceholder'))}"
            autocomplete="off"
            spellcheck="false"
          />
        </div>
        <div class="command-palette-results" id="command-palette-results"></div>
      </div>
    </div>
  `;
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.firstElementChild;
}

/** Injects the palette stylesheet once (idempotent — guarded by element id).
 * Every color is a token so the palette follows the light/dark theme. */
function ensureStylesheet() {
  if (document.getElementById('command-palette-style')) return;
  const style = document.createElement('style');
  style.id = 'command-palette-style';
  style.textContent = `
    .command-palette-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 12vh;
      z-index: 9999;
      font-family: var(--font-b);
    }

    .command-palette-modal {
      background: var(--card-bg);
      backdrop-filter: var(--card-blur);
      -webkit-backdrop-filter: var(--card-blur);
      border: 1px solid var(--card-border);
      border-radius: var(--radius);
      width: 90%;
      max-width: 520px;
      max-height: 60vh;
      display: flex;
      flex-direction: column;
      box-shadow: var(--dropdown-shadow);
      overflow: hidden;
    }

    .command-palette-header {
      padding: 14px 16px;
      border-bottom: 1px solid var(--border);
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .command-palette-header::before {
      content: '';
      width: 15px; height: 15px;
      flex-shrink: 0;
      background: var(--text-f);
      -webkit-mask: no-repeat center / contain url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E");
      mask: no-repeat center / contain url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E");
    }

    .command-palette-input {
      width: 100%;
      background: transparent;
      border: none;
      outline: none;
      color: var(--text);
      font-size: 15px;
      font-weight: 500;
    }

    .command-palette-input::placeholder {
      color: var(--text-f);
    }

    .command-palette-results {
      overflow-y: auto;
      flex: 1;
      min-height: 0;
    }

    .command-palette-results::-webkit-scrollbar {
      width: 6px;
    }

    .command-palette-results::-webkit-scrollbar-thumb {
      background: var(--accent-d);
      border-radius: 3px;
    }

    .command-palette-results::-webkit-scrollbar-track {
      background: transparent;
    }

    .command-palette-item {
      padding: 10px 16px;
      border-bottom: 1px solid var(--border);
      cursor: pointer;
      transition: background 0.15s ease;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .command-palette-item:hover {
      background: var(--accent-g);
    }

    .command-palette-item.selected {
      background: var(--accent-glow);
    }

    .command-palette-item-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      color: var(--accent-b);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .command-palette-item-icon svg {
      width: 100%;
      height: 100%;
    }

    .command-palette-item-text {
      flex: 1;
      min-width: 0;
    }

    .command-palette-item-label {
      color: var(--text);
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .command-palette-item-route {
      color: var(--text-f);
      font-family: var(--font-mono);
      font-size: 11px;
      font-weight: 400;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 2px;
      direction: ltr;
      unicode-bidi: isolate;
    }

    .command-palette-item-group {
      color: var(--accent-b);
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      flex-shrink: 0;
      margin-inline-start: auto;
    }

    .command-palette-empty {
      padding: 32px 16px;
      text-align: center;
      color: var(--text-f);
      font-size: 13px;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Mount command palette: register Cmd/Ctrl+K listener, manage modal lifecycle.
 * navSections: array of { label, items:[{label, path, icon}] } groups.
 */
export function mountCommandPalette(navSections) {
  ensureStylesheet();

  // Cleanup prior mount's listeners
  if (paletteAbort) paletteAbort.abort();
  paletteAbort = new AbortController();
  const { signal } = paletteAbort;

  let modal = null;
  let selectedIdx = 0;
  const flatItems = navSections.flatMap((s) =>
    s.items.map((item) => ({ ...item, group: s.label }))
  );

  const openPalette = () => {
    if (modal) return; // Already open

    modal = createPaletteDOM();
    document.body.appendChild(modal);

    const inputEl = modal.querySelector('#command-palette-input');
    const resultsEl = modal.querySelector('#command-palette-results');

    selectedIdx = 0;
    renderResults('');

    inputEl.focus();
    inputEl.addEventListener('input', (e) => {
      selectedIdx = 0;
      renderResults(e.target.value);
    });

    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closePalette();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIdx = Math.min(selectedIdx + 1, resultsEl.children.length - 1);
        syncSelection();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIdx = Math.max(selectedIdx - 1, 0);
        syncSelection();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = resultsEl.children[selectedIdx];
        if (selected) {
          const path = selected.dataset.path;
          closePalette();
          navigate(path);
        }
      }
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closePalette();
    });

    function renderResults(query) {
      const scored = flatItems
        .map((item) => ({
          ...item,
          score: fuzzyScore(item.label + ' ' + item.path, query),
        }))
        .filter((item) => item.score > -Infinity)
        .sort((a, b) => b.score - a.score);

      if (scored.length === 0) {
        resultsEl.innerHTML = `<div class="command-palette-empty">${escapeHtml(t('palette.noResults'))}</div>`;
        return;
      }

      resultsEl.innerHTML = scored
        .map((item, idx) => {
          const isSelected = idx === selectedIdx ? ' selected' : '';
          return `
            <div class="command-palette-item${isSelected}" data-path="${item.path}" data-idx="${idx}">
              <div class="command-palette-item-icon">${item.icon}</div>
              <div class="command-palette-item-text">
                <div class="command-palette-item-label">${escapeHtml(item.label)}</div>
                <div class="command-palette-item-route">${escapeHtml(item.path)}</div>
              </div>
              <div class="command-palette-item-group">${escapeHtml(item.group)}</div>
            </div>
          `;
        })
        .join('');

      // Wire click handlers
      resultsEl.querySelectorAll('.command-palette-item').forEach((el) => {
        el.addEventListener('click', () => {
          const path = el.dataset.path;
          closePalette();
          navigate(path);
        });
        el.addEventListener('mouseenter', () => {
          selectedIdx = parseInt(el.dataset.idx, 10);
          syncSelection();
        });
      });
    }

    function syncSelection() {
      resultsEl.querySelectorAll('.command-palette-item').forEach((el, idx) => {
        el.classList.toggle('selected', idx === selectedIdx);
      });
      // Auto-scroll selected item into view
      const selected = resultsEl.children[selectedIdx];
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  const closePalette = () => {
    if (modal) {
      modal.remove();
      modal = null;
    }
  };

  // Expose this mount's open() to openCommandPalette (top-bar search box).
  _openPalette = openPalette;

  // Cmd/Ctrl+K listener
  document.addEventListener(
    'keydown',
    (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openPalette();
      }
    },
    { signal }
  );

  // Close on Escape (document-level)
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && modal) {
        closePalette();
      }
    },
    { signal }
  );
}

/** Opens the command palette programmatically (used by the top-bar search
 * box). No-ops until mountCommandPalette has run at least once. */
export function openCommandPalette() {
  if (_openPalette) _openPalette();
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function escapeAttrCp(text) {
  return String(text == null ? '' : text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
